import { onMounted, onUnmounted } from 'vue';
import { SafeAreaColorsEvent } from '~/shared/NativeBridge/events/SafeAreaColorsEvent';

/**
 * No app iOS, o nativo pinta a faixa de safe-area do topo (notch/Dynamic
 * Island) com a cor que o site reporta. Aqui a gente amostra a cor EFETIVA no
 * topo do conteudo — compondo a pilha de fundos naquele ponto (incl. backdrops
 * de modal/menu) sobre a cor base do tema — e despacha pro nativo quando muda.
 *
 * Amostra num poll leve (nao via MutationObserver) porque modais que abrem/
 * fecham por opacity/transition nao mexem no DOM — sem o poll, a faixa ficava
 * com a cor do modal grudada apos fechar. Elementos invisiveis (opacity 0 /
 * visibility hidden) sao ignorados; a opacidade computada entra na composicao,
 * entao a faixa acompanha o fade. Blur nao tem cor: cai na aproximacao (o que
 * esta atras). Fora do app, `dispatch()` e no-op.
 */
export function useNativeSafeAreaColor() {
  const layoutStore = useLayoutStore();

  let lastColor = '';
  let pollId: ReturnType<typeof setInterval> | null = null;

  function parseRgb(str: string): [number, number, number, number] | null {
    const m = str.match(/rgba?\(([^)]+)\)/i);

    if (!m) return null;

    const parts = m[1].split(',').map((s) => parseFloat(s));
    const [r, g, b] = parts;
    const a = parts.length >= 4 ? parts[3] : 1;

    if ([r, g, b].some((n) => Number.isNaN(n))) return null;

    return [r, g, b, Number.isNaN(a) ? 1 : a];
  }

  function themeBase(): [number, number, number] {
    return layoutStore.isDarkTheme ? [2, 7, 22] : [10, 14, 27];
  }

  function effectiveTopColor(): string {
    const [br, bg, bb] = themeBase();
    let r = br;
    let g = bg;
    let b = bb;

    if (typeof document !== 'undefined') {
      const x = Math.floor(window.innerWidth / 2);
      const stack = document.elementsFromPoint(x, 2);

      // Compoe do fundo pro topo (pilha visual).
      for (let i = stack.length - 1; i >= 0; i--) {
        const style = getComputedStyle(stack[i]);
        const elOpacity = parseFloat(style.opacity || '1');

        // Invisivel: nao contribui (ex.: backdrop de modal saindo/oculto).
        if (style.visibility === 'hidden' || elOpacity <= 0.01) continue;

        const rgba = parseRgb(style.backgroundColor);

        if (!rgba || rgba[3] <= 0) continue;

        const a = rgba[3] * elOpacity;

        r = rgba[0] * a + r * (1 - a);
        g = rgba[1] * a + g * (1 - a);
        b = rgba[2] * a + b * (1 - a);
      }
    }

    return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
  }

  function tick() {
    const top = effectiveTopColor();

    if (top && top !== lastColor) {
      lastColor = top;
      new SafeAreaColorsEvent({ top }).dispatch();
    }
  }

  onMounted(() => {
    tick();
    // Poll leve: elementsFromPoint + getComputedStyle em poucos elementos;
    // so despacha quando a cor muda. Cobre abrir/fechar/fade de modal, troca
    // de tema e navegacao sem depender de eventos especificos.
    pollId = setInterval(tick, 100);
  });

  onUnmounted(() => {
    if (pollId) clearInterval(pollId);
  });
}
